import React from 'react';
import { graphql } from 'gatsby';
import { PageMetadata } from '../components/structure/head';
import { getParagraph } from '../components/paragraphs/paragraphs';
import AdminInfo from '../components/structure/admin-info';
import { MailChimpOptInFormBlocks } from '../components/blocks/mailchimp-opt-in-form';
import { MessageSomePagesBlock } from '../components/blocks/message-some-pages';
import PageTitleSection from '../components/structure/page-header';
import { SiteWideMessageBlock } from '../components/blocks/site-wide-message';
import { TileImagesSomePagesBlocks } from '../components/blocks/tile-images-some-pages';
import { createPageParentTrail } from '../functions/build_menus/build-menu-tree';
import {
  randomKey,
  convertKeyToReadableText,
  convertArrayToReadableText,
  stripUrlParkPrefix,
} from '../functions/common';
import { DevAids } from '../components/dev-aids';
import Link from '../components/elements/link';

export default function ProjectTemplate({ data, pageContext }) {
  const park = process.env.GATSBY_PARK,
    thisPage = data.nodeProject,
    isLive = thisPage.status,
    state = thisPage.moderation_state,
    pageType = thisPage.node_type.drupal_internal__target_id || null,
    nodeID = thisPage.drupal_internal__nid,
    title = thisPage.title,
    path = pageContext?.alias || null,
    projectType = convertArrayToReadableText(thisPage.field_project_type) || null,
    projectStatus = convertKeyToReadableText(thisPage.field_project_status) || null,
    startDate = thisPage.field_start_date,
    endDate = thisPage.field_end_date,
    region = thisPage.relationships?.field_kakadu_region || null,
    timeframe = thisPage.field_timeframe || null,
    externalLink = thisPage.field_external_resources_link || null,
    breadcrumbData =
      createPageParentTrail(pageContext.siteMenuData, nodeID, path, pageType, title) || null,
    paragraphs = thisPage.relationships.paragraphs.map((item) => getParagraph(item, pageContext));

  const listOfLinks = (items) => {
    return (
      items
        .sort((a, b) => {
          return a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1;
        })
        .map((item, index) => {
          let str = index === 0 ? '' : ', ';
          return (
            <React.Fragment key={`${index}-${item.title}`}>
              {str}
              <Link to={stripUrlParkPrefix(item.path?.alias)}>{item.title}</Link>
            </React.Fragment>
          );
        }) || []
    );
  };
  const relatedPlaces = thisPage.relationships?.field_related_places
      ? listOfLinks(thisPage.relationships?.field_related_places)
      : [],
    regionLinks = region ? listOfLinks(region) : [];

  return (
    <>
      <PageMetadata
        title={title}
        description={thisPage.body?.summary || title}
        thumbnailImage={null}
        path={path}
        dateModified={thisPage.changed}
      />

      {/* If not the homepage, add the title header and social links */}
      {path !== '' ? (
        <PageTitleSection
          title={title}
          socialMediaBlock={data.blockContentSocialMedia}
          breadcrumbs={breadcrumbData}
          summaryText={thisPage?.body?.summary || null}
        />
      ) : (
        ''
      )}

      {/* Main content */}
      <main id="content">
        <div className="grid-wrapper">
          <div className="grid-row clearfix">
            <div className="grid-col grid-col--8 grid-col--push-2 tb-grid-col--10 tb-grid-col--push-1 ph-grid-col--12 ph-grid-col--push-0">
              <div className="copy">
                {/* Show an Edit link for Gatsby Preview environments */}
                <AdminInfo status={isLive} moderationState={state} path={path} />
                <DevAids node={thisPage} pageLinks={true} />

                <SiteWideMessageBlock
                  callingPark={park}
                  nodes={data.allBlockContentSiteWideMessage}
                />

                <MessageSomePagesBlock
                  nodeID={nodeID}
                  nodes={data.allBlockContentMessageSomePages}
                  callingPark={park}
                />

                <div className="project-info mb-medium">
                  <hr className="mb-xsmall" />
                  {projectType && (
                    <p>
                      <strong>Project type:</strong> <span>{projectType}</span>
                    </p>
                  )}
                  {/* {startDate && !endDate && (
                    <p>
                      <strong>Start date:</strong> {formatDateToAEST(startDate)}
                    </p>
                  )}
                  {startDate && endDate && (
                    <p>
                      <strong>Project duration:</strong> {formatDateToAEST(startDate)} -{' '}
                      {formatDateToAEST(endDate)}
                    </p>
                  )} */}
                  {timeframe && (
                    <p>
                      <strong>Delivery timeframe:</strong> {timeframe}
                    </p>
                  )}
                  {projectStatus && (
                    <p>
                      <strong>Status:</strong> {projectStatus}
                    </p>
                  )}
                  {regionLinks.length > 0 && (
                    <p>
                      {regionLinks.length > 1 ? (
                        <strong>Regions:</strong>
                      ) : (
                        <strong>Region:</strong>
                      )}{' '}
                      {regionLinks}
                    </p>
                  )}
                  {relatedPlaces.length > 0 && (
                    <p>
                      <strong>Related locations:</strong> {relatedPlaces}
                    </p>
                  )}
                  {externalLink && (
                    <p>
                      <strong>More information:</strong>{' '}
                      <Link to={externalLink.uri}>
                        {externalLink.title || 'External resources'}
                      </Link>
                    </p>
                  )}
                  <hr className="mt-xsmall" />
                </div>

                {thisPage.body?.processed ? (
                  <div
                    className="body-content"
                    dangerouslySetInnerHTML={{
                      __html: thisPage.body?.processed,
                    }}
                  />
                ) : (
                  <React.Fragment key={randomKey()}></React.Fragment>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Paragraphs */}
        {paragraphs}
      </main>

      {/* Mailchimp Subscribe forms */}
      <MailChimpOptInFormBlocks
        nodes={data.allBlockContentMailchimpOptInForm}
        pagePath={pageContext.alias}
      />

      {/* Tile images on some pages Block */}
      <TileImagesSomePagesBlocks
        nodes={data.allBlockContentTileImagesSomePages}
        pagePath={pageContext.alias}
      />
    </>
  );
}

// This queries pages by their Drupal ID (UUID), not their Node ID
export const query = graphql`
  query ($park: String!, $drupalId: String!) {
    nodeProject(drupal_id: { eq: $drupalId }) {
      id
      title
      path {
        alias
      }
      drupal_id
      drupal_internal__nid
      body {
        value
        summary
        processed
      }
      changed
      created
      field_site {
        drupal_internal__target_id
      }
      field_project_type
      field_project_status
      field_start_date
      field_end_date
      field_timeframe
      field_external_resources_link {
        title
        uri
      }
      moderation_state
      node_type {
        drupal_internal__target_id
      }
      status
      relationships {
        field_kakadu_region {
          title
          path {
            alias
          }
        }
        field_related_places {
          title
          path {
            alias
          }
        }
        paragraphs: field_paragraphs {
          type: __typename
          # ...ParagraphAccessReportText
          # ...ParagraphAccordionContainer
          # ...ParagraphButtonsContainer
          # ...ParagraphDownloadsList
          # ...ParagraphEmbedIframe
          # ...ParagraphEmbedMap
          # ...ParagraphEmbedScript
          # ...ParagraphEmbedVideo
          # ...ParagraphFeedList
          ...ParagraphFigure
          # ...ParagraphGalleryGrid
          # ...ParagraphHeroImageSection
          # ...ParagraphQuoteWithImage
          # ...ParagraphSectionHeadingLinks
          # ...ParagraphPeople
          ...ParagraphTable
          # ...ParagraphTeaserList
          ...ParagraphText
          # ...ParagraphTileImages
        }
      }
    }
    allBlockContentSiteWideMessage(
      filter: {
        field_site_for_block: { drupal_internal__target_id: { eq: $park } }
        field_enabled: { eq: true }
      }
    ) {
      ...SiteWideMessageBlockQuery
    }
    allBlockContentMessageSomePages(
      filter: {
        field_site_for_block: { drupal_internal__target_id: { eq: $park } }
        field_enabled: { eq: true }
      }
    ) {
      ...MessageSomePagesBlockQuery
    }
    allBlockContentTileImagesSomePages(
      filter: {
        field_site_for_block: { drupal_internal__target_id: { eq: $park } }
        field_enabled: { eq: true }
      }
    ) {
      ...TileImagesSomePagesBlockQuery
    }
    blockContentSocialMedia {
      ...SocialMediaLinksBlockQuery
    }
    allBlockContentMailchimpOptInForm(
      filter: {
        field_site_for_block: { drupal_internal__target_id: { eq: $park } }
        field_enabled: { eq: true }
      }
    ) {
      ...MailChimpOptInFormBlockQuery
    }
  }
`;
